import { createSlice } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import { ListOutreachConfig } from "../../pages/List/schema/list-outreach-config.schema";

import { listOutreachApi } from "./list-outreach.api";

export interface ListOutreachState {
	/**
	 * The fetched config per list id.
	 *
	 * Bucketed by list rather than held as a single "current list" object: RTK Query serves a
	 * warm cache entry **without** re-dispatching `fulfilled`, so revisiting a list whose config
	 * is still cached would leave a single bucket showing whichever list was fetched last
	 * (apps/web CLAUDE.md §2).
	 */
	configByListId: Record<number, ListOutreachConfig>;
}

const initialState: ListOutreachState = {
	configByListId: {},
};

export const listOutreachSlice = createSlice({
	name: "listOutreach",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		// Both matchers file the payload under the list it was REQUESTED for
		// (`meta.arg.originalArgs`), never under whichever list is currently open: a response can
		// land after the user has already navigated to another list.
		builder.addMatcher(
			listOutreachApi.endpoints.getListOutreachConfig.matchFulfilled,
			(state, { payload, meta }) => {
				state.configByListId[meta.arg.originalArgs] = payload;
			}
		);
		builder.addMatcher(
			listOutreachApi.endpoints.updateListOutreachConfig.matchFulfilled,
			(state, { payload, meta }) => {
				state.configByListId[meta.arg.originalArgs.listId] = payload;
			}
		);
	},
});

export const listOutreachReducer = listOutreachSlice.reducer;

export const listOutreachSelector = (state: RootState): ListOutreachState =>
	state[listOutreachSlice.name];

/**
 * A list's outreach config as the slice holds it — `undefined` until that list has been fetched
 * once. Always read the config through this rather than from the query hook, so a warm cache
 * needs no `fulfilled` to fire.
 */
export const listOutreachConfigSelector =
	(listId: number | undefined) =>
	(state: RootState): ListOutreachConfig | undefined =>
		listId === undefined ? undefined : state[listOutreachSlice.name].configByListId[listId];
